/**
 * Список вариантов техкарт сезона — точка входа экрана (макет v2-05, блок
 * «Варианты этой техкарты», развёрнутый на весь сезон).
 *
 * Группировка по культуре: агроном мыслит «озимая пшеница — три варианта», а
 * не плоским списком из сорока карт. ₽/га стоит прямо в строке, потому что
 * вариант выбирают по деньгам.
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Stack } from '@/components/ui/stack';
import {
  CARD_STATUS_TITLES,
  formatNumber,
  formatPerHa,
  type TechCard,
} from '@/lib/planning/techCards';

export function VariantsList({
  cards,
  cropNames,
  onOpen,
  onClone,
  canClone,
  activeCardId,
}: {
  cards: TechCard[];
  cropNames: Record<string, string>;
  onOpen: (card: TechCard) => void;
  onClone: (card: TechCard) => void;
  canClone: boolean;
  activeCardId?: string | null;
}) {
  const byCrop = new Map<string, TechCard[]>();
  for (const card of cards) {
    const list = byCrop.get(card.crop_id) ?? [];
    list.push(card);
    byCrop.set(card.crop_id, list);
  }
  const groups = [...byCrop.entries()]
    .map(([cropId, list]) => ({
      cropId,
      title: cropNames[cropId] ?? 'культура без названия в справочнике',
      list: [...list].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    }))
    .sort((a, b) => a.title.localeCompare(b.title, 'ru'));

  return (
    <Stack gap={4}>
      {groups.map((group) => (
        <section key={group.cropId} aria-labelledby={`crop-${group.cropId}`}>
          <div className="mb-2 flex items-center gap-2">
            <h2 id={`crop-${group.cropId}`} className="text-sm font-semibold text-foreground">
              {group.title}
            </h2>
            <span className="text-xs text-muted-foreground">
              {group.list.length === 1 ? '1 вариант' : `${group.list.length} варианта`}
            </span>
          </div>
          <Stack gap={2}>
            {group.list.map((card) => (
              <Card key={card.id} className={card.id === activeCardId ? 'border-primary' : undefined}>
                <CardContent className="p-0">
                  {/*
                    ОДНА точка входа на карточку: кликабельна вся карточка.
                    Раньше открыть вариант можно было двумя узкими целями —
                    текстом названия и кнопкой внизу, — а сама карточка кликов
                    не принимала: попадание мимо текста выглядело как «не
                    открылось с первого раза» (P1 founder 20.08). Кнопка
                    «Клонировать» — СОСЕДНИЙ элемент, а не вложенный: кнопка
                    внутри кнопки невалидна и съедает клики.
                  */}
                  {/* aria-label — короткая метка для скринридера: иначе он читает
                      всю начинку карточки как одно длинное название кнопки. */}
                  <button
                    type="button"
                    onClick={() => onOpen(card)}
                    aria-label={`Открыть вариант «${card.name}»`}
                    className={
                      canClone
                        ? 'w-full rounded-t-lg p-4 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        : 'w-full rounded-lg p-4 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    }
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold text-foreground">{card.name}</span>
                      {card.status !== 'draft' && (
                        <Badge variant={card.status === 'ready' ? 'primary' : 'outline'}>
                          {CARD_STATUS_TITLES[card.status]}
                        </Badge>
                      )}
                      <span className="ml-auto text-base font-semibold text-foreground">
                        {formatPerHa(card.cost?.per_ha ?? null)}
                      </span>
                    </span>
                    <span className="flex min-h-10 flex-wrap items-center gap-x-6 gap-y-1 py-2 text-sm leading-6 text-muted-foreground">
                      <span>
                        {card.target_yield_c_ha === null
                          ? 'плановая урожайность не задана'
                          : `плановая урожайность ${formatNumber(card.target_yield_c_ha)} ц/га`}
                      </span>
                      <span>
                        {card.scenarios_count > 0
                          ? `назначен вариантам плана: ${card.scenarios_count}`
                          : 'ни одному варианту плана не назначен'}
                      </span>
                      <span>
                        {card.cost && card.cost.gaps.length > 0
                          ? `посчитано не всё: пробелов ${card.cost.gaps.length}`
                          : 'себестоимость посчитана полностью'}
                      </span>
                    </span>
                    <span className="text-sm font-medium text-primary">Открыть вариант →</span>
                  </button>
                  {canClone && (
                    <div className="border-t border-border px-4 py-2">
                      <Button variant="outline" size="sm" onClick={() => onClone(card)}>
                        Клонировать вариант
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </section>
      ))}
    </Stack>
  );
}
